import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRightIcon, FolderPlusIcon, RefreshCwIcon } from 'lucide-react';
import { type JSX, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button.js';
import { canAddArtifact } from '@/features/artifacts/artifact-display.js';
import { removeFolderFromList } from '@/features/microsoft/remove-from-list.js';
import {
  oneDriveAccountQueryOptions,
  oneDriveKeys,
  oneDriveScopesQueryOptions,
  updateOneDriveScope,
  type OneDriveScope,
  type OneDriveScopeBudget,
} from '@/lib/api/onedrive.js';
import {
  getWorkspaceConnectorAuthUrl,
  listWorkspaceConnectors,
  workspaceConnectorKeys,
  workspaceConnectorsQueryOptions,
  type WorkspaceConnectorConnection,
} from '@/lib/api/workspace-connectors.js';

import { OneDriveFileManager } from './OneDriveFileManager.js';
import { OneDriveKnowledgeRemoveDialog } from './OneDriveKnowledgeRemoveDialog.js';
import {
  oneDriveErrorPresentation,
  type OneDriveErrorPresentation,
} from './onedrive-error.js';
import { useOneDriveKnowledgeLookup } from './onedrive-knowledge.js';
import { pollOneDriveReconnect } from './onedrive-reconnect.js';
import { OneDriveScopeList } from './OneDriveScopeList.js';
import { withCreatedScope } from './scope-budget-cache.js';
import { OneDriveSetupDialog } from './OneDriveSetupDialog.js';

function OneDriveErrorDetails({
  error,
}: {
  error: OneDriveErrorPresentation;
}): JSX.Element {
  return (
    <div className="text-sm">
      <p className="text-destructive">{error.message}</p>
      <p className="text-muted-foreground">{error.remediation}</p>
    </div>
  );
}

/**
 * What a folder is called here: the alias its owner gave it, or the name it carries in OneDrive. The
 * whole drive is called what it is — Graph names its root `root`, a word nobody chose.
 */
function scopeName(scope: OneDriveScope): string {
  if (scope.isDriveRoot) return 'The entire OneDrive';
  return scope.alias ?? scope.originalName;
}

export function OneDriveTab({
  workspaceId,
  role,
  scopeId,
  folderId,
  itemId,
  onNavigate,
  onAddToChat,
}: {
  workspaceId: string;
  /**
   * The caller's role in this workspace, and the only thing that decides whether a folder can be put into
   * the workspace's knowledge base from here. It is OPTIONAL because the composer's attachment dialog
   * renders this tab too, to attach a file to a message — offering a knowledge registration there would be
   * a third way in that nothing asked for, so that surface passes no role and the action never appears.
   */
  role?: string | null;
  scopeId: string | null;
  folderId: string | null;
  itemId: string | null;
  onNavigate: (
    scopeId: string | null,
    folderId: string | null,
    itemId: string | null,
  ) => void;
  onAddToChat?: (
    scopeId: string,
    item: import('@/lib/api/onedrive.js').OneDriveItem,
  ) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const scopesQuery = useQuery(oneDriveScopesQueryOptions(workspaceId));
  const scopes = scopesQuery.data ?? [];
  const matchedScope = scopeId
    ? (scopes.find((scope) => scope.scopeId === scopeId) ?? null)
    : null;
  /**
   * The account of the connection this tab's folders come from.
   *
   * A person can reach several Microsoft accounts now, so "their OneDrive" has no single answer.
   * The folders name the connection they belong to: an open folder is read through its own
   * connection, and the list falls back to the first folder's — with no folders there is no account
   * to show either.
   */
  const scopeConnectionId = (matchedScope ?? scopes[0])?.connectionId ?? '';
  const accountQuery = useQuery(
    oneDriveAccountQueryOptions(workspaceId, scopeConnectionId),
  );
  const connectorsQuery = useQuery(
    workspaceConnectorsQueryOptions(workspaceId),
  );
  const [setupOpen, setSetupOpen] = useState(false);
  /** The registered folder waiting to be taken back off the shelf, with the source that holds it. */
  const [knowledgeRemoval, setKnowledgeRemoval] = useState<{
    sourceId: string;
    name: string;
    isFile: boolean;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<OneDriveErrorPresentation | null>(null);

  /**
   * Whether this caller may add to the workspace's knowledge base: owner or editor, the same rule every
   * other knowledge surface applies. Read from the role this tab was given, so a surface that gives none
   * offers nothing.
   */
  const canAddToKnowledge = canAddArtifact(role ?? null);
  /**
   * What the workspace already indexes out of these folders, so a row offers the way in or the way out
   * rather than the same way twice. A folder is on the shelf when its OWN root is registered — a
   * subfolder added through the knowledge picker is a different source, and this row cannot speak for it.
   */
  const knowledgeLookup = useOneDriveKnowledgeLookup({
    workspaceId,
    role: role ?? null,
  });
  const knowledgeSourceForScope = (scope: OneDriveScope) =>
    knowledgeLookup({ scopeId: scope.scopeId, itemId: scope.rootItemId });
  const account = accountQuery.data ?? null;
  const selectedScope = matchedScope;
  /**
   * The caller's own personal OneDrive-capable connections, all of which Add folder offers. The list is
   * not narrowed to the account shown above: since reads span the workspace, the first folder can sit on
   * another member's connection, and narrowing to it would leave the caller nothing to add with.
   */
  const personalConnections = useMemo(
    () =>
      (connectorsQuery.data ?? [])
        .filter((connector) => connector.provider === 'sharepoint')
        .flatMap((connector) => connector.connections)
        .filter(
          (connection): connection is WorkspaceConnectorConnection =>
            connection.scope === 'personal' &&
            connection.credentialType === 'oauth_token' &&
            connection.connectionId.length > 0,
        ),
    [connectorsQuery.data],
  );

  useEffect(() => {
    if (scopesQuery.data === undefined) return;
    if ((scopeId && !matchedScope) || (!scopeId && (folderId || itemId))) {
      onNavigate(null, null, null);
    }
  }, [folderId, itemId, matchedScope, onNavigate, scopeId, scopesQuery.data]);

  async function invalidateAll(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: oneDriveKeys.all }),
      queryClient.invalidateQueries({
        queryKey: workspaceConnectorKeys.list(workspaceId),
      }),
    ]);
  }

  /**
   * Signs the account in again with the access its connection holds now. The access is read fresh from
   * identity, because a cached copy may predate a change made since and would widen it back.
   */
  async function reconnect(): Promise<void> {
    if (!account) return;
    setBusy(true);
    setError(null);
    try {
      const stored = (await listWorkspaceConnectors(workspaceId))
        .flatMap((connector) => connector.connections)
        .find((connection) => connection.connectionId === account.connectionId);
      const response = await getWorkspaceConnectorAuthUrl(
        workspaceId,
        'sharepoint',
        stored?.permissionLevel ?? account.permissionLevel,
        account.connectionId,
        stored?.allowDelete,
      );
      const popup = window.open(
        response.auth_url,
        'cti-onedrive-reconnect',
        'popup,width=560,height=720,noopener,noreferrer',
      );
      const connected = await pollOneDriveReconnect(
        workspaceId,
        account.connectionId,
      );
      if (connected) popup?.close();
      await invalidateAll();
      if (!connected) {
        setError({
          message: 'OneDrive reconnect was not confirmed.',
          remediation:
            'Finish authorization for this Microsoft connection, then try reconnecting again.',
          canRetry: true,
          operationCompleted: false,
        });
      }
    } catch (reason) {
      setError(oneDriveErrorPresentation(reason));
    } finally {
      setBusy(false);
    }
  }

  async function updateScope(
    targetScopeId: string,
    patch: Parameters<typeof updateOneDriveScope>[1] extends infer Input
      ? Omit<Input & object, 'workspaceId'>
      : never,
  ): Promise<void> {
    await updateOneDriveScope(targetScopeId, { workspaceId, ...patch });
    await invalidateAll();
  }

  async function removeScope(targetScopeId: string): Promise<void> {
    await removeFolderFromList(queryClient, {
      workspaceId,
      scopeId: targetScopeId,
    });
    if (selectedScope?.scopeId === targetScopeId) onNavigate(null, null, null);
    await invalidateAll();
  }

  function created(scope: OneDriveScope): void {
    queryClient.setQueryData<OneDriveScopeBudget>(
      oneDriveKeys.scopes(workspaceId),
      (current) => withCreatedScope(current, scope),
    );
    onNavigate(scope.scopeId, null, null);
    void invalidateAll();
  }

  if (
    accountQuery.isLoading ||
    scopesQuery.isLoading ||
    connectorsQuery.isLoading
  ) {
    return <p className="text-sm text-muted-foreground">Loading OneDrive…</p>;
  }

  if (accountQuery.isError || scopesQuery.isError || connectorsQuery.isError) {
    const loadPresentation = oneDriveErrorPresentation(
      accountQuery.error ?? scopesQuery.error ?? connectorsQuery.error,
    );
    return (
      <div role="alert" className="border-l-2 border-destructive py-2 pl-4">
        <p className="font-medium text-destructive">
          {loadPresentation.message}
        </p>
        <p className="text-sm text-muted-foreground">
          {loadPresentation.remediation}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Work with selected folders and files from your personal OneDrive.
          Interactive and automation access are separate, and files remain in
          Microsoft 365.
        </p>
        <Button
          disabled={account !== null && account.status !== 'active'}
          onClick={() => setSetupOpen(true)}
        >
          <FolderPlusIcon /> Add
        </Button>
      </div>

      {account && account.status !== 'active' && (
        <div
          role="alert"
          className="flex flex-col gap-2 border-l-2 border-destructive py-2 pl-4"
        >
          <p className="font-medium text-destructive">
            {account.status === 'reconsent_required'
              ? 'Reconnect required'
              : 'OneDrive unavailable'}
          </p>
          <p className="text-sm text-muted-foreground">
            Access is paused until the personal Microsoft connection is
            restored.
          </p>
          <div>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => void reconnect()}
            >
              <RefreshCwIcon /> Reconnect account
            </Button>
          </div>
          {error && <OneDriveErrorDetails error={error} />}
        </div>
      )}

      {!account && (
        <div className="px-1 py-3">
          <p className="font-medium">Nothing from OneDrive connected</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Add a folder or a file with a caller-owned personal SharePoint OAuth
            connection. A workspace or service connection is never used as
            fallback.
          </p>
          {personalConnections.length === 0 && (
            <p className="mt-2 text-sm text-muted-foreground">
              Open Connectors in your personal space and allow all workspaces,
              or open the workspace where your personal connection is available.
            </p>
          )}
        </div>
      )}

      {account && scopes.length > 0 ? (
        <div
          data-testid="onedrive-browser-layout"
          className="flex flex-col gap-3"
        >
          {account.status !== 'active' || !selectedScope ? (
            <OneDriveScopeList
              account={account}
              scopes={scopes}
              selectedScopeId={null}
              onSelect={(nextScopeId) => onNavigate(nextScopeId, null, null)}
              onUpdate={updateScope}
              onDelete={removeScope}
              {...(canAddToKnowledge
                ? {
                    knowledge: {
                      isRegistered: (scope: OneDriveScope) =>
                        knowledgeSourceForScope(scope) !== undefined,
                      onRemove: (scope: OneDriveScope) => {
                        const source = knowledgeSourceForScope(scope);
                        if (source) {
                          setKnowledgeRemoval({
                            sourceId: source.id,
                            name: scopeName(scope),
                            isFile: scope.isFile,
                          });
                        }
                      },
                    },
                  }
                : {})}
            />
          ) : (
            <>
              <nav
                className="flex flex-wrap items-center gap-1 text-sm"
                aria-label="OneDrive path"
              >
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground hover:underline"
                  onClick={() => onNavigate(null, null, null)}
                >
                  OneDrive
                </button>
                <ChevronRightIcon
                  className="size-4 text-muted-foreground"
                  aria-hidden
                />
                <span className="font-medium text-foreground">
                  {scopeName(selectedScope)}
                </span>
              </nav>
              <OneDriveFileManager
                key={`${selectedScope.scopeId}:${folderId ?? ''}`}
                workspaceId={workspaceId}
                account={account}
                scope={selectedScope}
                scopes={scopes}
                folderId={folderId}
                itemId={itemId}
                onNavigate={(nextFolderId, nextItemId) =>
                  onNavigate(
                    selectedScope.scopeId,
                    nextFolderId,
                    nextItemId ?? null,
                  )
                }
                onAddToChat={
                  onAddToChat
                    ? (item) => onAddToChat(selectedScope.scopeId, item)
                    : undefined
                }
              />
            </>
          )}
        </div>
      ) : account?.status === 'active' ? (
        <div className="px-1 py-3 text-sm text-muted-foreground">
          This account has no connected folders or files in this workspace. Add
          one to start.
        </div>
      ) : null}

      {error && account?.status === 'active' && (
        <OneDriveErrorDetails error={error} />
      )}

      {knowledgeRemoval && (
        <OneDriveKnowledgeRemoveDialog
          workspaceId={workspaceId}
          sourceId={knowledgeRemoval.sourceId}
          name={knowledgeRemoval.name}
          isFile={knowledgeRemoval.isFile}
          onClose={() => setKnowledgeRemoval(null)}
        />
      )}

      {/*
        The whole-drive grant covers every item, so it stays out of what the picker counts as
        covered: there it would mark everything "Included" and nothing could be added.
      */}
      {setupOpen && (
        <OneDriveSetupDialog
          open
          workspaceId={workspaceId}
          connections={personalConnections}
          scopes={scopes.filter((scope) => !scope.openGrant)}
          onOpenChange={setSetupOpen}
          onCreated={created}
        />
      )}
    </div>
  );
}
